import { Link, useSearchParams } from "react-router";
import { consoleApi, type ConsoleSearchHit } from "../../../lib/admin-console-api";
import { useAppPreferences } from "../../../lib/preferences";
import { LoadState, PageHeader, Section, StatusChip, useLoad } from "../ui";

const RECORD_PATHS: Record<ConsoleSearchHit["kind"], string> = {
  provider: "/admin/providers",
  venue: "/admin/venues",
  account: "/admin/accounts",
};

function HitList({ hits, empty }: { hits: ConsoleSearchHit[]; empty: string }) {
  if (hits.length === 0) return <p className="console-muted">{empty}</p>;
  return (
    <ul className="console-list">
      {hits.map((hit) => (
        <li key={hit.id}>
          <Link className="console-list-row" to={`${RECORD_PATHS[hit.kind]}/${hit.id}`}>
            <span className="min-w-0 flex-1">
              <span className="console-list-title">{hit.title}</span>
              {hit.subtitle ? <span className="console-muted console-truncate">{hit.subtitle}</span> : null}
            </span>
            {hit.status ? <StatusChip status={hit.status} /> : null}
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function SearchPage() {
  const { t } = useAppPreferences();
  const [params] = useSearchParams();
  const query = (params.get("q") ?? "").trim();
  const results = useLoad(
    () => (query.length >= 2 ? consoleApi.search(query) : Promise.resolve(null)),
    [query],
  );
  const total = results.data
    ? results.data.providers.length + results.data.venues.length + results.data.accounts.length
    : 0;

  return (
    <div className="console-page">
      <PageHeader
        description={query.length < 2 ? t("Type at least 2 characters in the search box.") : t("{count} matches for “{query}”", { count: total, query })}
        title={t("Search")}
      />
      <LoadState error={results.error} isEmpty={!results.data} loading={results.loading} onRetry={() => void results.reload()} empty={t("Search by business or venue name, email, phone, or an ID.")}>
        {results.data ? (
          <div className="console-grid-3">
            <Section title={t("Providers")}>
              <HitList empty={t("No providers match.")} hits={results.data.providers} />
            </Section>
            <Section title={t("Venues")}>
              <HitList empty={t("No venues match.")} hits={results.data.venues} />
            </Section>
            <Section title={t("Accounts")}>
              <HitList empty={t("No accounts match.")} hits={results.data.accounts} />
            </Section>
          </div>
        ) : null}
      </LoadState>
    </div>
  );
}
